import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { theme } from '@kardume/theme';
import { NotificationProvider } from '@kardume/hooks';
import { authService, backofficeApi } from '@kardume/services';
import type { BackofficeUserDto } from '@kardume/types';
import { LoginScreen } from '@kardume/ui';
import BackofficeLayout from './layouts/BackofficeLayout';
import ChangePasswordModal from './components/ChangePasswordModal';
import { BackofficeRoutes } from './routes';
import {
  BackofficeSectionId,
  getInitialSection,
  getVisibleNavItems,
} from './utils/access';

const SECTION_KEY = '@KardumeUrbano:backoffice:activeSection';

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<BackofficeUserDto | null>(null);
  const [pendingChange, setPendingChange] = useState<{ email: string; currentPassword: string } | null>(null);
  const [activeSection, setActiveSection] = useState<BackofficeSectionId>(
    () => (localStorage.getItem(SECTION_KEY) as BackofficeSectionId) || 'dashboard'
  );

  useEffect(() => {
    localStorage.setItem(SECTION_KEY, activeSection);
  }, [activeSection]);

  const isAdmin = user?.role === 'ADMIN';
  const permissions = useMemo(() => user?.effectivePermissions ?? [], [user]);
  const navigationItems = useMemo(
    () => getVisibleNavItems(permissions, isAdmin),
    [permissions, isAdmin]
  );

  const loadSession = async () => {
    try {
      const { user: me } = await backofficeApi.getMe();
      setUser(me);
      const next = getInitialSection(
        me.effectivePermissions,
        activeSection,
        me.role === 'ADMIN'
      );
      if (next) setActiveSection(next);
    } catch {
      authService.logout('backoffice');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authService.isAuthenticated()) {
      loadSession();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (email: string, password: string) => {
    const result = await authService.backofficeLogin({ email, password });
    if (result.authStatus === 'PASSWORD_CHANGE_REQUIRED') {
      setPendingChange({ email, currentPassword: password });
      return;
    }
    await loadSession();
  };

  const handleLogout = () => {
    authService.logout('backoffice');
    setUser(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <Toaster position="top-right" />
        {loading ? (
          <div style={{ minHeight: '100vh' }} />
        ) : !user ? (
          <>
            <LoginScreen
              panelTitle="Central de operações do Kardume Urbano."
              panelSubtitle="Gerencie lojistas, usuários, planos e toda a plataforma em um só lugar."
              formTitle="Backoffice"
              onSubmit={handleLogin}
              onForgotPassword={authService.backofficeForgotPassword}
            />
            {pendingChange && (
              <ChangePasswordModal
                email={pendingChange.email}
                currentPassword={pendingChange.currentPassword}
                onClose={() => setPendingChange(null)}
                onSuccess={async () => {
                  setPendingChange(null);
                  await loadSession();
                }}
              />
            )}
          </>
        ) : (
          <BackofficeLayout
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            onLogout={handleLogout}
            userName={`${user.firstName} ${user.lastName}`}
            navigationItems={navigationItems}
          >
            <BackofficeRoutes activeSection={activeSection} permissions={permissions} isAdmin={isAdmin} />
          </BackofficeLayout>
        )}
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
